import type {
    CreateOrganizationRequestBody,
    CreateOrganizationResponse,
    GetOrganizationListRequestQuery,
    GetOrganizationListResponse,
    GetOrganizationRequestParams,
    GetOrganizationResponse,
    UpdateOrganizationRequestBody,
    UpdateOrganizationResponse,
} from '@engagement-web-app/common';
import { APIError } from './types';

export async function fetchOrganizations({
    from,
    limit,
    search,
    assignedConsultantIds,
    industryIds,
    prefectureIds,
    companySizeIds,
}: GetOrganizationListRequestQuery): Promise<GetOrganizationListResponse> {
    try {
        const query = new URLSearchParams();
        if (from !== undefined) query.set('from', String(from));
        if (limit !== undefined) query.set('limit', String(limit));
        if (search) query.set('search', search);
        if (assignedConsultantIds) query.set('assignedConsultantIds', JSON.stringify(assignedConsultantIds));
        if (industryIds) query.set('industryIds', JSON.stringify(industryIds));
        if (prefectureIds) query.set('prefectureIds', JSON.stringify(prefectureIds));
        if (companySizeIds) query.set('companySizeIds', JSON.stringify(companySizeIds));

        const res = await fetch(`/api/system/organizations?${query}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchOrganization({ uuid }: GetOrganizationRequestParams): Promise<GetOrganizationResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${uuid}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function updateOrganization(
    { uuid }: GetOrganizationRequestParams,
    body: UpdateOrganizationRequestBody
): Promise<UpdateOrganizationResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${uuid}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function createOrganization(body: CreateOrganizationRequestBody): Promise<CreateOrganizationResponse> {
    try {
        const res = await fetch('/api/system/organizations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export interface ToggleMfaResponse {
    mfaEnabled: boolean;
    updatedUsers: number;
}

export async function toggleOrganizationMfa(organizationId: string, enabled: boolean): Promise<ToggleMfaResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/mfa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
